import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { usePillars, pillarChipStyle } from "@/contexts/PillarsContext";
import { CompactThumbnailPicker } from "@/components/admin/ThumbnailControls";
import { Plus, Trash, PencilSimple, Check, X, ArrowUp, ArrowDown } from "@phosphor-icons/react";

const PALETTE = [
  "#CCFF00", "#FF5100", "#00E5FF", "#CC50FF",
  "#FFCB00", "#00FF94", "#FF3D8E", "#5C8BFF",
  "#FF7A45", "#7C7CFF",
  "#E63946", "#2A9D8F", "#9B5DE5", "#8AC926",
  "#F72585", "#3A86FF", "#FB8500", "#06D6A0",
];

export default function PillarsPanel() {
  const { pillars, settings, refresh, termPlural } = usePillars();
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [color, setColor] = useState(PALETTE[0]);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState(PALETTE[0]);
  const [term, setTerm] = useState({ singular: settings.pillar_term_singular || "Pillar", plural: settings.pillar_term_plural || "Pillars", intro: settings.intro_column_label || "Main Course Introduction" });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [info, setInfo] = useState("");

  // Resync local form state if the global settings change (admin can update from elsewhere)
  useEffect(() => {
    setTerm({ singular: settings.pillar_term_singular || "Pillar", plural: settings.pillar_term_plural || "Pillars", intro: settings.intro_column_label || "Main Course Introduction" });
  }, [settings.pillar_term_singular, settings.pillar_term_plural]);

  const create = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.post("/admin/pillars", { name: name.trim(), color });
      setName(""); setColor(PALETTE[0]); setShowForm(false);
      await refresh();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const startEdit = (p) => {
    setEditingId(p.id); setEditName(p.name); setEditColor(p.color);
  };

  const saveEdit = async (id) => {
    setBusy(true); setErr("");
    try {
      await api.patch(`/admin/pillars/${id}`, { name: editName.trim(), color: editColor });
      setEditingId(null);
      await refresh();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (p) => {
    setBusy(true); setErr(""); setInfo("");
    try {
      const { data } = await api.delete(`/admin/pillars/${p.id}`);
      setConfirmDeleteId(null);
      await refresh();
      if (data.cleared_courses > 0 || data.library_assets_unassigned > 0) {
        setInfo(`Deleted "${p.name}". Courses removed: ${data.cleared_courses} · Library assets unassigned: ${data.library_assets_unassigned}`);
      }
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= pillars.length) return;
    const reordered = [...pillars];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(target, 0, moved);
    setBusy(true);
    try {
      await api.post("/admin/pillars/reorder", reordered.map((p) => p.id));
      await refresh();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const setPillarThumbnail = async (id, url) => {
    setErr("");
    try {
      await api.patch(`/admin/pillars/${id}`, { thumbnail_url: url });
      await refresh();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const saveTerm = async () => {    if (!term.singular.trim() || !term.plural.trim()) return;
    setBusy(true); setErr("");
    try {
      await api.patch("/admin/settings", {
        pillar_term_singular: term.singular.trim(),
        pillar_term_plural: term.plural.trim(),
        intro_column_label: (term.intro || "").trim() || "Main Course Introduction",
      });
      await refresh();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Terminology */}
      <div className="card" data-testid="pillar-terminology-card">
        <h2 className="text-xl font-bold font-display mb-1">Section terminology</h2>
        <p className="text-[#000000] text-sm mb-4">
          Rename &ldquo;{settings.pillar_term_singular}&rdquo; globally — every label, dropdown and heading in the app will follow.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-[#000000] mb-1">Singular</div>
            <input
              data-testid="term-singular-input"
              className="input-base w-full"
              value={term.singular}
              onChange={(e) => setTerm({ ...term, singular: e.target.value })}
              placeholder="Pillar / Module / Chapter / Section"
            />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-[#000000] mb-1">Plural</div>
            <input
              data-testid="term-plural-input"
              className="input-base w-full"
              value={term.plural}
              onChange={(e) => setTerm({ ...term, plural: e.target.value })}
              placeholder="Pillars / Modules / Chapters / Sections"
            />
          </div>
          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.15em] text-[#000000] mb-1">First column / intro name</div>
            <input
              data-testid="intro-label-input"
              className="input-base w-full"
              value={term.intro}
              onChange={(e) => setTerm({ ...term, intro: e.target.value })}
              placeholder="Main Course Introduction"
            />
            <div className="text-[11px] text-[#000000] mt-1">Name of the first matrix column (lessons with no {term.singular.toLowerCase()}). Shown to students above the intro lessons.</div>
          </div>
        </div>
        <button
          data-testid="save-term-button"
          className="btn-primary mt-3"
          disabled={busy}
          onClick={saveTerm}
        >
          Save terminology
        </button>
      </div>

      {/* Pillar list */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold font-display">{termPlural} ({pillars.length})</h2>
          <button
            data-testid="new-pillar-button"
            className="btn-primary flex items-center gap-1"
            onClick={() => setShowForm((s) => !s)}
          >
            <Plus size={16} weight="bold" /> New {term.singular.toLowerCase()}
          </button>
        </div>

        {err && <div className="text-[#FF5100] mb-3 text-sm">{err}</div>}
        {info && (
          <div className="mb-3 text-sm px-3 py-2 rounded-lg bg-[#ECFDF5] border border-[#A7F3D0] text-[#065F46] flex items-center justify-between" data-testid="pillar-info">
            <span>{info}</span>
            <button className="text-[#065F46] underline text-xs" onClick={() => setInfo("")}>Dismiss</button>
          </div>
        )}

        {showForm && (
          <form onSubmit={create} className="mb-5 p-4 rounded-2xl bg-[#DCDEE2] border border-[#C4C8CE] flex flex-col gap-3" data-testid="new-pillar-form">
            <input
              data-testid="new-pillar-name"
              className="input-base"
              placeholder={`${term.singular} name (e.g. Introduction)`}
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-[#000000] mb-2">Colour</div>
              <div className="flex flex-wrap gap-2" data-testid="color-palette">
                {PALETTE.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    data-testid={`color-${c.replace("#", "")}`}
                    className="w-9 h-9 rounded-full transition-all"
                    style={{
                      background: c,
                      transform: color === c ? "scale(1.15)" : "scale(1)",
                      outline: color === c ? "2px solid #fff" : "none",
                      outlineOffset: "2px",
                      cursor: "pointer",
                      border: "none",
                    }}
                  />
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button data-testid="submit-new-pillar" type="submit" className="btn-primary" disabled={busy || !name.trim()}>
                Create
              </button>
              <button type="button" className="btn-secondary" onClick={() => { setShowForm(false); setName(""); }}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {pillars.length === 0 && !showForm && (
          <div className="text-[#000000] text-sm py-6 text-center">
            No {termPlural.toLowerCase()} yet. Create your first one to start building courses.
          </div>
        )}

        <div className="flex flex-col gap-2">
          {pillars.map((p, idx) => (
            <div
              key={p.id}
              data-testid={`pillar-row-${p.slug}`}
              className="flex items-center gap-3 p-3 rounded-xl bg-[#DCDEE2] border border-[#C4C8CE]"
            >
              <div className="flex flex-col gap-0.5">
                <button data-testid={`move-up-${p.slug}`} className="btn-ghost p-1" disabled={idx === 0 || busy} onClick={() => move(idx, -1)}>
                  <ArrowUp size={14} />
                </button>
                <button data-testid={`move-down-${p.slug}`} className="btn-ghost p-1" disabled={idx === pillars.length - 1 || busy} onClick={() => move(idx, 1)}>
                  <ArrowDown size={14} />
                </button>
              </div>

              {editingId === p.id ? (
                <>
                  <input
                    data-testid={`edit-name-${p.slug}`}
                    className="input-base flex-1"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                  />
                  <div className="flex gap-1 flex-wrap max-w-[280px]">
                    {PALETTE.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setEditColor(c)}
                        className="w-7 h-7 rounded-full"
                        style={{
                          background: c,
                          transform: editColor === c ? "scale(1.15)" : "scale(1)",
                          outline: editColor === c ? "2px solid #fff" : "none",
                          outlineOffset: "1px",
                          cursor: "pointer", border: "none",
                        }}
                      />
                    ))}
                  </div>
                  <button data-testid={`save-edit-${p.slug}`} className="btn-primary p-2" onClick={() => saveEdit(p.id)} disabled={busy || !editName.trim()}>
                    <Check size={16} weight="bold" />
                  </button>
                  <button className="btn-secondary p-2" onClick={() => setEditingId(null)}>
                    <X size={16} weight="bold" />
                  </button>
                </>
              ) : (
                <>
                  <span className="chip text-sm font-semibold" style={pillarChipStyle(p.color)}>
                    {p.name}
                  </span>
                  <code className="text-xs text-[#000000] flex-1">slug: {p.slug}</code>
                  <CompactThumbnailPicker
                    value={p.thumbnail_url}
                    onChange={(url) => setPillarThumbnail(p.id, url)}
                    testidPrefix={`pillar-thumb-${p.slug}`}
                  />
                  <button data-testid={`edit-pillar-${p.slug}`} className="btn-ghost p-2" onClick={() => startEdit(p)} title="Rename / recolor">
                    <PencilSimple size={16} />
                  </button>
                  {confirmDeleteId === p.id ? (
                    <div className="flex items-center gap-2 pl-1" data-testid={`confirm-delete-${p.slug}`}>
                      <span className="text-xs text-[#B91C1C] max-w-[280px]">Delete? Any course using this {term.singular.toLowerCase()} is removed; library assets are unassigned.</span>
                      <button data-testid={`confirm-delete-yes-${p.slug}`} className="btn-secondary text-xs leading-none" style={{ padding: "4px 12px", background: "#B91C1C", color: "#fff", borderColor: "#B91C1C" }} disabled={busy} onClick={() => remove(p)}>Delete</button>
                      <button className="btn-ghost text-xs text-[#000000]" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                    </div>
                  ) : (
                    <button data-testid={`delete-pillar-${p.slug}`} className="btn-ghost p-2 text-[#FF5100]" onClick={() => setConfirmDeleteId(p.id)} title="Delete">
                      <Trash size={16} />
                    </button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
